import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { getVideoSceneFrameUrl } from "@/api/renderer/api";
import { VideoInsightScene } from "@/api/studio/insights/types/attention.type";
import { cn } from "@/utils/classes";

/* Prop Types */
interface VideoScenesStripProps {
  projectId: string;
  studyId: string;
  videoKey: string;
  scenes: VideoInsightScene[];
  selectedSceneKey: string | null;
  onSelectScene: (sceneKey: string) => void;
}

/**
 * VideoScenesStrip component
 *
 * Renders a horizontal strip of scene cards (thumbnail, number, and duration) below the attention
 * graph. Clicking a card selects it, highlighting its border; the selected scene's detail renders
 * separately in VideoSceneDetail.
 */
export const VideoScenesStrip = ({ projectId, studyId, videoKey, scenes, selectedSceneKey, onSelectScene }: VideoScenesStripProps) => {
  if (scenes.length === 0) return null;

  /* Render */
  return (
    <div className="flex flex-col gap-3 bg-[#242c35] rounded-lg p-4 min-w-0">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-semibold">Scenes</h2>
        <div className="flex items-center gap-1.5 opacity-70">
          <span className="text-white text-xs font-semibold">About scenes</span>
          <InformationCircleIcon className="size-3.5 text-white" />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto min-w-0">
        {scenes.map((scene, index) => {
          const isSelected = scene.scene === selectedSceneKey;
          return (
            <button
              key={scene.scene}
              type="button"
              onClick={() => onSelectScene(scene.scene)}
              aria-pressed={isSelected}
              className={cn(
                "flex flex-col items-start gap-1.5 w-[89px] shrink-0 rounded-lg border-2 px-1 py-2 cursor-pointer text-left",
                isSelected ? "border-white" : "border-[#323c49]",
              )}
            >
              {scene.frames[0] ? (
                <img
                  src={getVideoSceneFrameUrl({ projectId, studyId, assetKey: videoKey, frameName: scene.frames[0] })}
                  alt={`Scene ${index + 1}`}
                  className="w-full h-11 rounded object-cover"
                />
              ) : (
                <div className="w-full h-11 rounded bg-[#323c49]" />
              )}
              <span className="text-white text-xs font-semibold px-1">Scene {index + 1}</span>
              <span className="text-[#9ca3af] text-xs font-medium px-1">{scene.duration.toFixed(2)}s</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
